import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useSelector, shallowEqual, useDispatch } from 'react-redux';
import ProjectForm from '#component/project/form';
import { updateBreadcrumb } from '#action/breadcrumb';
import { addProject, fetchProjectsUsersDevices } from '#action/project/dashboard';
import { CLEAR_PROJECT_DATA } from '#action/types';
import { putProjectAlerts, fetchProjectAlerts } from '#action/project/alerts';
import { putProjectProducts, fetchProjectProducts } from '#action/project/products';
import { putProjectPackages, fetchProjectPackages } from '#action/project/packages';
import { putProjectCustomFields, fetchProjectCustomFields } from '#action/project/custom-fields';
import { putProjectAlertConfirmations, fetchProjectAlertConfirmations } from '#action/project/alert-confirmations';
import useMinMaxDate from '#component/project/hooks/useMinMaxDate';
import { getInitialModules, customFieldsToInitValues, formatAlertDelayIntoFullTime } from '#component/project/form/helper';
import { StyledPeloaderComponent } from './styles';
import propTypes from './prop-types';
import {
  listToTree,
  setDocumentTitle,
  sortUsers,
  isUndefinedOrNull,
  sortProjects,
  getMillisecondsFromTimeDelta
} from '#helper';

const AddProjectForm = props => {
  const [loading, setLoading] = useState(true);
  const { history } = props;
  const dispatch = useDispatch();
  const account = useSelector(s => s.account, shallowEqual);
  const {
    users: unsortedUsers = [],
    projects: unsortedProjects = []
  } = useSelector(s => s.projectsUsersDevices, shallowEqual);
  const [selectedParentProject, setSelectedParentProject] = useState();
  const projectsAlerts = useSelector(s => s.projectsAlerts, shallowEqual);
  const projectAlertConfirmations = useSelector(s => s.projectAlertConfirmations, shallowEqual);
  const projectsProducts = useSelector(s => s.projectsProducts, shallowEqual);
  const projectsPackages = useSelector(s => s.projectsPackages, shallowEqual);
  const projectCustomFields = useSelector(s => s.projectCustomFields, shallowEqual);

  const users = useMemo(
    () => sortUsers(unsortedUsers),
    [unsortedUsers]
  );
  const projectsTree = useMemo(
    () =>
      listToTree(sortProjects(unsortedProjects), 0),
    [unsortedProjects]
  );

  const parentProject = useMemo(
    () =>
      unsortedProjects.find(up => up.id === selectedParentProject),
    [unsortedProjects, selectedParentProject]
  ) ?? projectsTree[0];

  const defaultUser = useMemo(() => ([{
    label: `${account.firstName} ${account.lastName}`,
    value: account.id
  }]), [account]);

  const minMaxDate = useMinMaxDate(parentProject);

  const initialValues = useMemo(() => {
    if (parentProject) {
      const projectAlerts = projectsAlerts[selectedParentProject] ?? [];
      const iV = {
        parentId: parentProject.id,
        beginDate: new Date().toISOString(),
        isExpirationDate: '0',
        users: defaultUser,
        alerts: projectAlerts,
        alertConfirmations: projectAlertConfirmations[selectedParentProject] ?? [],
        products: projectsProducts[selectedParentProject] ?? [],
        packages: projectsPackages[selectedParentProject] ?? [],
        modules: getInitialModules({}, parentProject)
      };

      if (parentProject.expirationDate) {
        iV.expirationDate = parentProject.expirationDate;
        iV.isExpirationDate = '1';
        iV.disableIsExpirationDate = true;
      }

      if (projectCustomFields.customFields) {
        iV.customFields = customFieldsToInitValues(projectCustomFields.customFields);
      }

      if (projectAlerts?.length) {
        iV.alerts = formatAlertDelayIntoFullTime(projectAlerts);
      }

      return iV;
    }

    return {};
  }, [
    parentProject,
    selectedParentProject,
    projectsAlerts,
    projectsProducts,
    projectsPackages,
    defaultUser,
    projectCustomFields.customFields,
    projectAlertConfirmations
  ]);

  useEffect(() => {
    setDocumentTitle('Add Project');
    dispatch(
      updateBreadcrumb([
        { label: 'Projects', path: '/project' },
        { label: 'Add project' }
      ])
    );
  }, [dispatch]);

  useEffect(() => {
    const fetchData = async () => {
      await Promise.all([
        dispatch(fetchProjectsUsersDevices())
      ]);
      setLoading(false);
    };

    fetchData();

    return () => {
      dispatch({ type: CLEAR_PROJECT_DATA });
    };
  }, [dispatch]);

  useEffect(() => {
    const fetchData = async () => {
      await Promise.all([
        dispatch(fetchProjectAlerts(selectedParentProject)),
        dispatch(fetchProjectAlertConfirmations(selectedParentProject)),
        dispatch(fetchProjectProducts(selectedParentProject)),
        dispatch(fetchProjectPackages(selectedParentProject)),
        dispatch(fetchProjectCustomFields(selectedParentProject))
      ]);
    };
    if (selectedParentProject) {
      fetchData();
    }
  }, [dispatch, selectedParentProject]);

  const handleParentProjectChange = useCallback(parentId => {
    setSelectedParentProject(parentId);
  }, []);

  const handleFormSubmit = async values => {
    const {
      title,
      expirationDate,
      parentId,
      beginDate,
      users: usersValues,
      products,
      packages,
      description,
      modules,
      customFields,
      alertConfirmations
    } = values;

    const data = {
      title,
      beginDate: beginDate.slice(0, 10),
      parentId,
      users: usersValues.map(user => user.value),
      modules
    };

    if (values.devices) {
      data.devices = values.devices.map(d => d.id);
    }

    if (expirationDate) {
      data.expirationDate = expirationDate.slice(0, 10);
    }

    if (description) {
      data.description = description;
    }

    let alerts = null;

    let productsForFetch = null;

    if (products?.length) {
      productsForFetch = products.map(product => {
        const valideProduct = { ...product };
        delete valideProduct.id;

        return valideProduct;
      });
    }

    let packagesForFetch = null;

    if (packages?.length) {
      packagesForFetch = packages.map(pack => {
        const validePackage = { ...pack };
        delete validePackage.id;
        delete validePackage.container;

        return validePackage;
      });
    }

    let customFieldsForFetch = null;

    if (customFields) {
      customFieldsForFetch = customFields.map(customField => {
        const { id, ...rest } = customField;

        return rest;
      });
    }

    if (values.alerts?.length) {
      const keys = ['tMin', 'tMax', 'hMin', 'hMax', 'bMin', 'startDelay', 'minAlertPeriod'];
      alerts = values.alerts.map(alert => {
        const transformedAlert = { label: alert.label };
        Object.keys(alert).forEach(key => {
          if (keys.includes(key) && !isUndefinedOrNull(alert[key])) {
            if (key === 'startDelay' || key === 'minAlertPeriod') {
              // Convert startDelay to milliseconds
              transformedAlert[key] = getMillisecondsFromTimeDelta(alert[key]);
            } else {
              transformedAlert[key] = Number((+alert[key]).toFixed(1));
            }
          }
        });
        return transformedAlert;
      });
    }

    let alertConfirmationsForFetch = null;
    if (alertConfirmations) {
      alertConfirmationsForFetch = alertConfirmations.map(alertConfirmation => {
        const { id, ...rest } = alertConfirmation;

        return rest;
      });
    }

    const { id } = await dispatch(addProject(data));
    const [alr, arlCnf, prd, pck, pcf] = await Promise.all([
      dispatch(putProjectAlerts(id, alerts ?? [])),
      dispatch(putProjectAlertConfirmations(id, alertConfirmationsForFetch ?? [])),
      dispatch(putProjectProducts(id, productsForFetch ?? [])),
      dispatch(putProjectPackages(id, packagesForFetch ?? [])),
      dispatch(putProjectCustomFields(id, customFieldsForFetch ?? []))
    ]);

    if (alr.success && arlCnf.success && prd.success && pck.success && pcf.success) {
      history.push('/project');
    }
  };

  return (
    !loading ? (
      <ProjectForm
        title="Add new project"
        {...props}
        initialValues={initialValues}
        arrayProjects={unsortedProjects}
        projects={projectsTree}
        users={users}
        minMaxDate={minMaxDate}
        onParentProjectChange={handleParentProjectChange}
        handleFormSubmit={handleFormSubmit}
        editable={true}
        account={account}
      />
    ) : (
      <StyledPeloaderComponent />
    )
  );
};

AddProjectForm.propTypes = propTypes;

export default AddProjectForm;
